/**
 * Quantity stepper: minus / value / plus in one outlined group. The one count control on web — guests in
 * the room search, extras at checkout, and the rooms of a rate on its card (`RoomCountPicker`), which
 * adds a spinner on the step waiting for the server and its own reasons a step is off.
 */

import React from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { Button } from '#ui/button.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';

export type StepperStep = 'decrement' | 'increment';

export interface StepperProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /** What sits between the steps, when the bare number is not enough ("2 rooms"). */
  label?: React.ReactNode;
  /** Accessible names for the steps, saying what they change ("Remove a room"). */
  decrementLabel?: string;
  incrementLabel?: string;
  /** Turns a step off for a reason of the caller's own (no rooms left), beyond `min` / `max`. */
  canDecrement?: boolean;
  canIncrement?: boolean;
  /** Both steps wait: something is in flight. */
  disabled?: boolean;
  /** The step waiting on the server: it spins, at full colour, so it reads as working. */
  pendingStep?: StepperStep;
  size?: 'sm' | 'default';
  /** Hide the outer outline. Use when nested inside an already-bordered container. */
  borderless?: boolean;
  /** Expand to fill the parent's width — the value centres in the space between the steps. */
  fullWidth?: boolean;
  'aria-label'?: string;
}

export function Stepper({
  value,
  onChange,
  min = 0,
  max = 99,
  label,
  decrementLabel = 'Decrease',
  incrementLabel = 'Increase',
  canDecrement = true,
  canIncrement = true,
  disabled = false,
  pendingStep,
  size = 'default',
  borderless = false,
  fullWidth = false,
  'aria-label': ariaLabel,
}: StepperProps): React.ReactNode {
  const stepSize = size === 'sm' ? 'icon-sm' : 'icon';
  const stepClass = (step: StepperStep) =>
    cn(
      STEP_CLASS,
      !borderless && STEP_EDGE_CLASS,
      !borderless && (step === 'decrement' ? '-ml-px rounded-r-none' : '-mr-px rounded-l-none'),
      pendingStep === step && SPINNING_STEP_CLASS,
    );
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={cn(
        'items-stretch rounded-sm bg-background',
        size === 'sm' ? 'h-9' : 'h-10',
        fullWidth ? 'flex w-full' : 'inline-flex',
        !borderless && 'border border-input',
      )}
    >
      <Button
        variant="ghost"
        size={stepSize}
        className={stepClass('decrement')}
        onClick={() => onChange(value - 1)}
        disabled={disabled || !canDecrement || value <= min}
        isBusy={pendingStep === 'decrement'}
        aria-label={decrementLabel}
      >
        <MaterialIcon icon="remove" size={IconSizes.sm} />
      </Button>
      <span
        className={cn(
          'flex min-w-0 items-center justify-center px-2 text-sm font-medium whitespace-nowrap tabular-nums',
          fullWidth ? 'flex-1' : 'min-w-8',
        )}
        aria-live="polite"
      >
        {label ?? value}
      </span>
      <Button
        variant="ghost"
        size={stepSize}
        className={stepClass('increment')}
        onClick={() => onChange(value + 1)}
        disabled={disabled || !canIncrement || value >= max}
        isBusy={pendingStep === 'increment'}
        aria-label={incrementLabel}
      >
        <MaterialIcon icon="add" size={IconSizes.sm} />
      </Button>
    </div>
  );
}

/**
 * A step: the ghost button's accent fill and light icon on hover, filling the group's full height. Faded
 * by its icon colour rather than opacity when disabled, so the group's outline stays solid around it.
 */
const STEP_CLASS = 'h-auto self-stretch disabled:opacity-100 disabled:text-foreground/30 disabled:hover:bg-transparent';

/**
 * Outlined, the steps overlap the outline by a pixel, so a hovered step fills to the group's edge instead
 * of sitting inside a lighter border. No dividers: the value between the steps sets them apart.
 */
const STEP_EDGE_CLASS = '-my-px';

/** The step waiting on the server is disabled too, but its spinner keeps full colour so it reads as working. */
const SPINNING_STEP_CLASS = 'disabled:text-foreground';
